import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import apiService from "../services/ApiService";
import { CommercialPlanDetails } from "./CurrentCommercialPlan";

import { Form, FormikProvider, useFormik } from "formik";
import { UTCOffset } from "./HistoryModal";
import { useAppStore } from "../shared/stores/useAppStore";
import { ORGANIZATION_QUERY_KEY, ORGANIZATION_USERS_QUERY_KEY } from "../shared/hooks/queries/useOrganization";

import TailwindButton from "./Tailwind/TailwindButton";
import TailwindModal from "./Tailwind/TailwindModal";
import TailwindSelect from "./Tailwind/TailwindSelect";
import { cn } from "../lib/utils";

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Helpers                                                           //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const getCurrentTzDate = (dateStr: string) => {
  const utcDate = new Date(dateStr);
  return new Date(utcDate.getTime() + UTCOffset * 60000);
};

const formatDate = (date: Date) => {
  if (useAppStore.getState().userSettings.dateSystem == 'DMY') {
    return date.toLocaleString('en-GB');
  } else {
    return date.toLocaleString('en-US');
  }
};

const getSelectedCommercialPlanInNewResp = (commercialPlans: any[], planId: number) => {
  return commercialPlans.find(p => p.id == planId) || null;
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Collapsible Section                                               //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function CollapsibleSection({ title, icon, defaultOpen = true, children }: {
  title: string;
  icon: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-5 py-4 bg-slate-50/50 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="material-symbols-outlined text-[20px] text-slate-500">{icon}</span>
        <span className="text-sm font-semibold text-slate-700 flex-1">{title}</span>
        <span className={cn(
          "material-symbols-outlined text-[20px] text-slate-400 transition-transform duration-200",
          isOpen && "rotate-180"
        )}>
          expand_more
        </span>
      </button>
      {isOpen && (
        <div className="px-5 py-4 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Licences Table                                                    //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function LicencesTable({ rows, onRemove }: { rows: any[]; onRemove: (userId: number) => void }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="size-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
          <span className="material-symbols-outlined text-[24px] text-slate-300">badge</span>
        </div>
        <p className="text-sm text-slate-500">No licences assigned yet</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Assignment Date</th>
            <th className="px-4 py-2.5 w-12" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((row: any) => (
            <tr key={row.id} className="group hover:bg-slate-50/50 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="size-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">
                    {(row.name?.[0] || '?').toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-slate-900">{row.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-slate-600">{row.email}</span>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">
                <span className="text-sm text-slate-400">
                  {row.assignment_date ? formatDate(getCurrentTzDate(row.assignment_date)) : '—'}
                </span>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onRemove(row.user_id)}
                  className="size-8 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                  title="Remove licence"
                >
                  <span className="material-symbols-outlined text-[18px] text-red-400 hover:text-red-600">close</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Models Table                                                      //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function ModelsTable({ rows }: { rows: any[] }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="size-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
          <span className="material-symbols-outlined text-[24px] text-slate-300">description</span>
        </div>
        <p className="text-sm text-slate-500">No models found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Owner</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((row: any) => (
            <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <span className="material-symbols-outlined text-[18px] text-slate-400">description</span>
                  <span className="text-sm font-medium text-slate-900">{row.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-slate-600">{row.owner_email}</span>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">
                <span className="text-sm text-slate-400">
                  {row.created_at ? formatDate(getCurrentTzDate(row.created_at)) : '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Assign Licence Modal                                              //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function AssignLicenceModal({ isOpen, onClose, eligibleUsers, onAssign }: {
  isOpen: boolean;
  onClose: () => void;
  eligibleUsers: { label: string; id: number }[];
  onAssign: (userId: number) => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = eligibleUsers.filter(u =>
    u.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAssign = () => {
    if (selectedUserId !== null) {
      onAssign(selectedUserId);
      setSelectedUserId(null);
      setSearchQuery('');
    }
  };

  const handleClose = () => {
    setSelectedUserId(null);
    setSearchQuery('');
    onClose();
  };

  return (
    <TailwindModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Assign Licence"
      description="Select a user to assign a licence to."
      size="sm"
      footer={
        <>
          <TailwindButton variant="ghost" onClick={handleClose}>
            Cancel
          </TailwindButton>
          <TailwindButton
            variant="primary"
            icon="badge"
            disabled={selectedUserId === null}
            onClick={handleAssign}
          >
            Assign
          </TailwindButton>
        </>
      }
    >
      <div className="space-y-3">
        {/* Search input */}
        <div className="relative">
          <span className="material-symbols-outlined text-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">
            search
          </span>
          <input
            type="text"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
            autoFocus
          />
        </div>

        {/* User list */}
        <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-50">
          {filteredUsers.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              {eligibleUsers.length === 0 ? 'No eligible users available' : 'No users match your search'}
            </div>
          ) : (
            filteredUsers.map(user => (
              <button
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                  selectedUserId === user.id
                    ? "bg-primary/5 border-l-2 border-l-primary"
                    : "hover:bg-slate-50 border-l-2 border-l-transparent"
                )}
              >
                <div className="size-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">
                  {user.label[0]?.toUpperCase() || '?'}
                </div>
                <span className={cn(
                  "text-sm truncate",
                  selectedUserId === user.id ? "font-medium text-primary" : "text-slate-700"
                )}>
                  {user.label}
                </span>
                {selectedUserId === user.id && (
                  <span className="material-symbols-outlined text-[18px] text-primary ml-auto shrink-0">check_circle</span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </TailwindModal>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Main Component                                                    //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

export default function CommercialPlanPanel({ organization, users, mounted }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [licenceAssignmentModalOpen, setLicenceAssignmentModalOpen] = useState(false);

  const [commercialPlans, setCommercialPlans] = useState<any[]>([]);
  const [selectedCommercialPlan, setSelectedCommercialPlan] = useState<any>({ id: 0, type: null });
  const [commercialPlanLicences, setCommercialPlanLicences] = useState<any>({});
  const [models, setModels] = useState<any[]>([]);

  // Load commercial plans
  useEffect(() => {
    apiService.getOrganizationCommercialPlans().then(async (response) => {
      if (response) {
        const resp = await response.json();
        setCommercialPlans(resp);
        if (resp.length && selectedCommercialPlan.id == 0) {
          setSelectedCommercialPlan(resp[0]);
        } else {
          setSelectedCommercialPlan(getSelectedCommercialPlanInNewResp(resp, selectedCommercialPlan.id));
        }
      }
    });
  }, [mounted]);

  // Load licences for selected plan
  useEffect(() => {
    if (selectedCommercialPlan.id != 0) {
      apiService.getCommercialPlanLicencesDetails(selectedCommercialPlan.id).then(async (response) => {
        if (response) {
          const resp = await response.json();
          setCommercialPlanLicences(resp);
        }
      });
    }
  }, [selectedCommercialPlan, mounted]);

  // Load models for selected plan
  useEffect(() => {
    if (selectedCommercialPlan.id != 0) {
      apiService.getCommercialPlanRelatedModels(selectedCommercialPlan.id).then(async (response) => {
        if (response) {
          const resp = await response.json();
          setModels(resp);
        }
      });
    }
  }, [selectedCommercialPlan, mounted]);

  const invalidateOrgData = () => {
    queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ORGANIZATION_USERS_QUERY_KEY });
  };

  const refreshLicenceData = async () => {
    invalidateOrgData();
    if (selectedCommercialPlan.id != 0) {
      const licencesResp = await apiService.getCommercialPlanLicencesDetails(selectedCommercialPlan.id);
      if (licencesResp) {
        setCommercialPlanLicences(await licencesResp.json());
      }
      const plansResp = await apiService.getOrganizationCommercialPlans();
      if (plansResp) {
        const plans = await plansResp.json();
        setCommercialPlans(plans);
        setSelectedCommercialPlan(getSelectedCommercialPlanInNewResp(plans, selectedCommercialPlan.id));
      }
    }
  };

  const handleRemoveLicence = (userId: number) => {
    apiService.unAssignLicenceFromUser(selectedCommercialPlan.id, userId).then(async (response) => {
      if (response) {
        await refreshLicenceData();
      }
    });
  };

  const handleAssignLicence = (userId: number) => {
    apiService.assignLicenceToUser(selectedCommercialPlan.id, userId).then(async (response) => {
      if (response) {
        setLicenceAssignmentModalOpen(false);
        await refreshLicenceData();
      }
    });
  };

  const getEligibleUsers = () => {
    const unassignedUsers = users.filter((user: any) => user.commercialPlanId == null);
    return unassignedUsers.map((e: any) => ({ label: e.emailAddress, id: e.id }));
  };

  const eligibleUsers = getEligibleUsers();

  const canAssignLicence = selectedCommercialPlan.type !== 'unitary'
    && commercialPlanLicences.licences_attributed < commercialPlanLicences.licences_count;

  const planOptions = commercialPlans.map((p: any) => ({
    value: p,
    label: p.name,
  }));

  // Licence usage ratio
  const licenceUsage = commercialPlanLicences.licences_count
    ? Math.round((commercialPlanLicences.licences_attributed / commercialPlanLicences.licences_count) * 100)
    : 0;

  return (
    <div>
      {/* Trigger Button */}
      <TailwindButton
        variant="secondary"
        size="sm"
        icon="workspace_premium"
        onClick={() => setModalOpen(true)}
      >
        Plans & Licences
      </TailwindButton>

      {/* Main Modal */}
      <TailwindModal
        isOpen={modalOpen}
        onClose={() => {
          // Don't close the parent modal if the child assign-licence modal is open
          if (!licenceAssignmentModalOpen) {
            setModalOpen(false);
          }
        }}
        title="Commercial Plans"
        description="Manage your organization's plans, licences, and models."
        size="2xl"
      >
        <div className="space-y-5">

          {/* Plan Selector */}
          <div className="max-w-xs">
            <TailwindSelect
              value={selectedCommercialPlan}
              onChange={(val) => setSelectedCommercialPlan(val)}
              options={planOptions}
              label="Active Plan"
              placeholder="Select a plan"
            />
          </div>

          {/* Details Section */}
          <CollapsibleSection title="Plan Details" icon="info" defaultOpen>
            <CommercialPlanDetails commercialPlan={selectedCommercialPlan} />
          </CollapsibleSection>

          {/* Licences Section */}
          <CollapsibleSection title="Licences" icon="badge" defaultOpen>
            <div className="space-y-4">
              {/* Licence Stats */}
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Total</p>
                    <p className="text-lg font-bold text-slate-900">{commercialPlanLicences.licences_count ?? '—'}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Assigned</p>
                    <p className="text-lg font-bold text-slate-900">{commercialPlanLicences.licences_attributed ?? '—'}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Available</p>
                    <p className="text-lg font-bold text-green-600">
                      {commercialPlanLicences.licences_count != null
                        ? commercialPlanLicences.licences_count - (commercialPlanLicences.licences_attributed || 0)
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className="flex-grow" />

                {/* Licence usage bar */}
                {commercialPlanLicences.licences_count > 0 && (
                  <div className="flex flex-col gap-1.5 min-w-[180px]">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Licence Usage</span>
                      <span className={cn(
                        "text-xs font-bold",
                        licenceUsage >= 100 ? "text-red-600" : licenceUsage >= 80 ? "text-amber-600" : "text-slate-700"
                      )}>
                        {commercialPlanLicences.licences_attributed}/{commercialPlanLicences.licences_count} ({licenceUsage}%)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-500",
                          licenceUsage >= 100 ? "bg-red-500" : licenceUsage >= 80 ? "bg-amber-500" : "bg-primary"
                        )}
                        style={{ width: `${Math.min(licenceUsage, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                <TailwindButton
                  variant="dark"
                  size="sm"
                  icon="person_add"
                  disabled={!canAssignLicence}
                  onClick={() => setLicenceAssignmentModalOpen(true)}
                >
                  Assign Licence
                </TailwindButton>
              </div>

              {/* Licences Table */}
              <LicencesTable
                rows={commercialPlanLicences.users_with_licences}
                onRemove={handleRemoveLicence}
              />
            </div>
          </CollapsibleSection>

          {/* Models Section */}
          <CollapsibleSection title="Models" icon="description" defaultOpen>
            <ModelsTable rows={models} />
          </CollapsibleSection>
        </div>
      </TailwindModal>

      {/* Assign Licence Modal */}
      <AssignLicenceModal
        isOpen={licenceAssignmentModalOpen}
        onClose={() => setLicenceAssignmentModalOpen(false)}
        eligibleUsers={eligibleUsers}
        onAssign={handleAssignLicence}
      />
    </div>
  );
}
